import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  GetFunctionsByEventId, GetGuestsByEventId, GetGuestsByEventIdForMember, GetCardTypes, GetCostLines,
} from '../api/api';
import { makeCounter, linesOf, linesTotal } from './costLines';
import { functionHeadcount } from './headcount';

// What every money screen needs to price a cost line LIVE: the functions, the
// guest list (a member sees only their own + the public guests, the same rule
// as every guest screen), the card types and the cost lines of the event.
//   count(source, functionId)  the live count a line follows (costLines.js)
//   linesFor(type, id)         the saved lines of one expense / service
//   liveTotal(type, id)        their total right now, or null when none
export function useEventCosts(event, enabled = true) {
  const { user, isAdmin } = useAuth();
  const me = user?.UserId;
  const [functions, setFunctions] = useState([]);
  const [guests, setGuests] = useState([]);
  const [cardTypes, setCardTypes] = useState([]);
  const [lines, setLines] = useState([]);

  const reloadLines = useCallback(() => {
    if (!enabled || !event?.EventId) return Promise.resolve();
    return GetCostLines({ EventId: event.EventId })
      .then(d => setLines(Array.isArray(d) ? d : []))
      .catch(() => setLines([]));          // an API without the table yet: no lines
  }, [event, enabled]);

  useEffect(() => {
    if (!enabled || !event?.EventId) return;
    GetFunctionsByEventId({ EventId: event.EventId }).then(d => setFunctions(Array.isArray(d) ? d : [])).catch(() => {});
    (isAdmin
      ? GetGuestsByEventId({ EventId: event.EventId })
      : GetGuestsByEventIdForMember({ EventId: event.EventId, UserId: me })
    ).then(d => setGuests(Array.isArray(d) ? d : [])).catch(() => {});
    GetCardTypes({ EventId: event.EventId }).then(d => setCardTypes(Array.isArray(d) ? d : [])).catch(() => {});
    reloadLines();
  }, [event, enabled, isAdmin, me, reloadLines]);

  const count = useMemo(() => makeCounter({ guests, cardTypes, functions }), [guests, cardTypes, functions]);
  const linesFor = useCallback((type, id) => linesOf(lines, type, id), [lines]);
  const liveTotal = useCallback((type, id) => {
    const own = linesOf(lines, type, id);
    return own.length ? linesTotal(own, count) : null;
  }, [lines, count]);
  const headcountOf = useCallback((fnId) => (fnId ? functionHeadcount(guests, fnId, cardTypes).persons : 0), [guests, cardTypes]);
  const fnName = useCallback((id) => functions.find(f => String(f.FunctionId) === String(id))?.FunctionName || '', [functions]);

  return { functions, guests, cardTypes, lines, count, linesFor, liveTotal, headcountOf, fnName, reloadLines };
}
